import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import PeopleIcon from '@mui/icons-material/People'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import DriveEtaIcon from '@mui/icons-material/DriveEta'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import { getLocalizedLabel } from '../utils/getLocalizedLabel'
import { formatCurrency } from '../utils/formatCurrency'
import { ResizableTableCell } from './ResizableTable'

interface Props {
  account: any
  isFreighter: boolean
  userCount: number | string
  score: number | string
  vehiclesCount: number
  driversCount: number
  kycPendingCount: number
  subscriptions: any[]
  planById?: Map<string, { name: string; planCode?: string }>
}

function Stat({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
  color?: string
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color ?? 'text.secondary',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {label}
        </Typography>
        <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  )
}

function subStatusColor(
  status: string,
): 'success' | 'info' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'ACTIVE':
      return 'success'
    case 'TRIAL':
      return 'info'
    case 'PAST_DUE':
      return 'warning'
    case 'CANCELLED':
    case 'EXPIRED':
      return 'error'
    default:
      return 'default'
  }
}

export default function AccountOverview({
  account,
  isFreighter,
  userCount,
  score,
  vehiclesCount,
  driversCount,
  kycPendingCount,
  subscriptions,
  planById,
}: Props) {
  if (!account) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select an account to view its overview.
      </Typography>
    )
  }

  const ratingNum = typeof account.rating === 'number' ? account.rating : null
  const ratingCount = account.ratingCount ?? 0

  return (
    <Stack spacing={2}>
      {/* Identity card */}
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {account.name || account.businessName || 'Unnamed account'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {account.businessName && account.businessName !== account.name
                  ? account.businessName
                  : null}
                {account.organizationCode ? ` · ${account.organizationCode}` : ''}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {account.type && (
                <Chip
                  label={getLocalizedLabel('accountType', account.type) || account.type}
                  size="small"
                  color={account.type === 'SHIPPER' ? 'info' : 'secondary'}
                />
              )}
              {account.status && (
                <Chip
                  label={getLocalizedLabel('accountStatus', account.status) || account.status}
                  size="small"
                  color={account.status === 'ACTIVE' ? 'success' : 'default'}
                />
              )}
            </Stack>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={3}>
              <Stat icon={<PeopleIcon fontSize="small" />} label="Users" value={userCount} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Stat icon={<StarBorderIcon fontSize="small" />} label="Score" value={score} />
            </Grid>
            {isFreighter && (
              <>
                <Grid item xs={6} sm={4} md={3}>
                  <Stat
                    icon={<LocalShippingIcon fontSize="small" />}
                    label="Vehicles"
                    value={vehiclesCount}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={3}>
                  <Stat
                    icon={<DriveEtaIcon fontSize="small" />}
                    label="Drivers"
                    value={driversCount}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={6} sm={4} md={3}>
              <Stat
                icon={<VerifiedUserIcon fontSize="small" />}
                label="KYC Pending"
                value={kycPendingCount}
                color={kycPendingCount > 0 ? 'warning.main' : undefined}
              />
            </Grid>
            {ratingNum != null && (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating value={ratingNum} readOnly size="small" precision={0.5} />
                  <Typography variant="caption" color="text.secondary">
                    {ratingNum.toFixed(1)} ({ratingCount})
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Contact / business snippet */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Contact & Business
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Contact Email
              </Typography>
              <Typography variant="body2">{account.contactEmail || '—'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Contact Phone
              </Typography>
              <Typography variant="body2">{account.contactPhone || '—'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                Business Address
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {account.businessAddress || '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">
                VAT / Legal ID
              </Typography>
              <Typography variant="body2">
                {account.vatNum || account.businessLegalId || '—'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Subscriptions section (moved from old Subscriptions tab) */}
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle2">Subscriptions</Typography>
            <Typography variant="caption" color="text.secondary">
              {subscriptions.length} subscription{subscriptions.length === 1 ? '' : 's'}
            </Typography>
          </Stack>
          {subscriptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No subscriptions for this account.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <ResizableTableCell>Plan Name</ResizableTableCell>
                    <ResizableTableCell>Status</ResizableTableCell>
                    <ResizableTableCell>Monthly Price</ResizableTableCell>
                    <ResizableTableCell>Started</ResizableTableCell>
                    <ResizableTableCell>Trial End</ResizableTableCell>
                    <ResizableTableCell>Next Billing</ResizableTableCell>
                    <ResizableTableCell>Discount</ResizableTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.map((sub: any) => {
                    const plan = planById?.get(sub.planId)
                    const planLabel = plan?.name || sub.planId?.substring(0, 8) || '—'
                    const planTooltip =
                      plan?.planCode && plan?.name
                        ? `${plan.name} (${plan.planCode})`
                        : plan?.name || sub.planId

                    return (
                    <TableRow key={sub.id} hover>
                      <TableCell>
                        <Tooltip title={planTooltip}>
                          <span>{planLabel}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            getLocalizedLabel('subscriptionStatus', sub.status) || sub.status
                          }
                          size="small"
                          color={subStatusColor(sub.status)}
                        />
                      </TableCell>
                      <TableCell>
                        {sub.monthlyPrice != null
                          ? `${formatCurrency(sub.monthlyPrice)} ${sub.currency || ''}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {sub.startDate ? new Date(sub.startDate).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        {sub.trialEndDate
                          ? new Date(sub.trialEndDate).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {sub.nextBillingDate
                          ? new Date(sub.nextBillingDate).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {sub.discountPercentage
                          ? `${sub.discountPercentage}%`
                          : sub.discountAmount
                          ? formatCurrency(sub.discountAmount)
                          : '—'}
                      </TableCell>
                    </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  )
}
